import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';

import Store from 'lib/Store/withLocalStorage';
import { useController } from 'pages/Meetings/hooks/useController';

import config from 'config/general';

import CardMessage from 'components/atom/CardMessage';
import { Wrapper } from 'components/atom/Grid';
import Loader from 'components/atom/Loader';

import { Title } from '../../styles';
import Filter from './components/Filters';
import ToolbarSearch from './components/ToolbarSearch';
import UserProspectionCard from './components/UserProspectionCard';
import { useSearchController } from './hooks/useSearchController';
import { Container, Content, SearchHeader } from './styles';

const authStore = Store(`${config.localStorageKey}-auth`);

const Search: React.FC = () => {
  const { t } = useTranslation();
  const {
    isLoading,
    prospectionListFiltered,
    cleanFilters,
    doRequest,
    nextPage,
    formik: { values },
  } = useSearchController();

  const { ref, inView } = useInView();

  const { setProspectionId, setPageActive, setProspectionListData } = useController();

  const handleInvite = useCallback(
    (id: string) => {
      setProspectionId(id);
      setPageActive('invite');
    },
    [setPageActive, setProspectionId],
  );

  useEffect(() => {
    doRequest(values, true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doRequest]);

  useEffect(() => {
    if (inView && nextPage && !isLoading) {
      doRequest(values, false, nextPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, nextPage, doRequest, isLoading]);

  const isApp = authStore.getItem('isApp') === 'true';

  return (
    <Container>
      {isApp && <ToolbarSearch isApp={isApp} />}

      <Filter />

      <Wrapper>
        {!isApp && (
          <SearchHeader>
            <Title>{t('meetings:navigation.search')}</Title>

            <ToolbarSearch isApp={isApp} />
          </SearchHeader>
        )}

        <Content>
          <p>
            {`${t('meetings:prospection.display')} ${prospectionListFiltered.length}
              ${t('meetings:prospection.contacts')}`}{' '}
            (
            <button type="button" onClick={cleanFilters}>
              {t('meetings:actions.cleanFilters')}
            </button>
            ):
          </p>

          {prospectionListFiltered && prospectionListFiltered.length > 0 ? (
            prospectionListFiltered.map((prospection) => (
              <UserProspectionCard
                key={prospection.id}
                {...prospection}
                handleInvite={() => {
                  handleInvite(prospection.id);
                  setProspectionListData(prospection);
                }}
              />
            ))
          ) : (
            <CardMessage message={t('meetings:prospection.info')} />
          )}

          {isLoading && <Loader />}
        </Content>

        {prospectionListFiltered.length > 0 && <div ref={ref} style={{ height: '1px' }} />}
      </Wrapper>
    </Container>
  );
};

export default Search;
